'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLiveScores } from '@/hooks/useLiveScores';
import { ChevronUp, ChevronDown, ArrowLeftRight, GripVertical, MessageSquare, Volume2, VolumeX } from 'lucide-react';
import { io } from 'socket.io-client';
import { getAuthToken } from '@/utils/auth';
import { decodePcm16Chunk, LIVE_AUDIO_ENCODING } from '@/lib/liveAudio';

const POS_KEY = 'kaympus.liveDock.pos';
const MIN_KEY = 'kaympus.liveDock.min';

/**
 * How long this has been running — the question a viewer actually has.
 * Returns null under a minute so the caller can say "just started" rather
 * than the nonsense "just started in".
 */
const elapsed = (from) => {
  if (!from) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(from).getTime()) / 60000));
  if (mins < 1) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
};

const clock = (d) =>
  d ? new Date(d).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '';

const read = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
};
const write = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
};

/** Stops a press on a control from starting a drag and eating the click. */
const noDrag = { onPointerDown: (e) => e.stopPropagation() };

/** How a finished match is coloured by its outcome. */
const OUTCOME_CLS = {
  Won: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Lost: 'bg-rose-50 text-rose-700 border-rose-200',
  Draw: 'bg-slate-100 text-slate-600 border-slate-200',
};

/**
 * Live dot, or the outcome of a match that has finished. Both states share
 * this slot so the board reads consistently whichever it is showing.
 */
const StatusTag = ({ match, compact = false }) => {
  if (match.isLive) {
    return (
      <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-bold uppercase tracking-wider text-red-600">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-blink" />
        {!compact && 'Live'}
      </span>
    );
  }
  const outcome = match.result?.outcome && match.result.outcome !== 'Pending' ? match.result.outcome : 'Draw';
  // "WON" on its own is written from the home side's point of view, so the
  // card never said WHO won — it read identically either way and the
  // viewer had to compare the two numbers themselves. Name the winner.
  // `winnerName` is 'Draw' for a level match and '' while still pending.
  const winner = match.winnerName && match.winnerName !== 'Draw' ? match.winnerName : '';
  const label = outcome === 'Draw' ? 'Draw' : (winner ? `${winner} won` : outcome);
  return (
    <span
      className={`inline-flex items-center shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${OUTCOME_CLS[outcome] || OUTCOME_CLS.Draw} ${compact ? '' : 'max-w-[11rem] truncate'}`}
      title={label}
      data-testid="live-dock-outcome"
    >
      {compact ? outcome.charAt(0) : label}
    </span>
  );
};

/**
 * The school's scoreboard, on every page.
 *
 * Mounted in the authenticated shell so a live match is visible wherever
 * you are, to EVERY role — students, parents, teachers and admins alike.
 *
 * Collapsed and expanded are the SAME control at two sizes: both can be
 * dragged anywhere, both switch between simultaneous matches, and both
 * name the match whose score is showing. Collapsing trades detail for
 * space, never capability — and it is always reversible.
 *
 * Shows today's board: matches in play, and matches that finished
 * today with their result. Renders nothing when there are neither.
 */
export default function LiveScoreDock() {
  const { matches } = useLiveScores(true, { fetchAll: true });
  const [open, setOpen] = useState(false);       // commentary drawer
  const [minimised, setMinimised] = useState(false);
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState(null);          // {x,y} once moved
  const dragRef = useRef(null);
  const panelRef = useRef(null);

  // Restore after mount — localStorage is unavailable during SSR, and
  // reading it in useState would desync the first paint.
  useEffect(() => {
    setMinimised(read(MIN_KEY, false));
    setPos(read(POS_KEY, null));
  }, []);

  const [unmutedFixtureId, setUnmutedFixtureId] = useState(null);
  const [hasLiveAudio, setHasLiveAudio] = useState(false);
  const activeMatch = matches[idx];
  const isMuted = unmutedFixtureId !== activeMatch?.id;

  const audioSocketRef = useRef(null);
  const unmutedFixtureIdRef = useRef(null);
  unmutedFixtureIdRef.current = unmutedFixtureId;
  const activeFixtureIdRef = useRef(null);
  activeFixtureIdRef.current = activeMatch?.id;

  const playbackContextRef = useRef(null);
  const nextPlaybackTimeRef = useRef(0);
  const playbackStreamIdRef = useRef(null);

  const initAudioPlayback = () => {
    if (typeof window === 'undefined') return;
    try {
      if (playbackContextRef.current) return playbackContextRef.current;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return null;
      const context = new AudioContextClass();
      playbackContextRef.current = context;
      nextPlaybackTimeRef.current = context.currentTime + 0.04;
      context.resume().catch(() => {});
      return context;
    } catch {
      return null;
    }
  };

  const resetAudioPlayback = () => {
    const context = playbackContextRef.current;
    playbackContextRef.current = null;
    nextPlaybackTimeRef.current = 0;
    playbackStreamIdRef.current = null;
    if (context) context.close().catch(() => {});
  };

  // Listen to background socket voice commentary stream across all pages
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return undefined;

    const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';
    const socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    audioSocketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('sports:live:join');
      const fixtureId = unmutedFixtureIdRef.current;
      if (fixtureId) socket.emit('sports:audio:listen', { fixtureId });
    });

    socket.on('sports:audio:status', ({ fixtureId, active }) => {
      console.log('[Web Dock 📡] Audio status event:', { fixtureId, active });
      if (fixtureId === activeFixtureIdRef.current) {
        setHasLiveAudio(!!active);
        if (!active) {
          playbackStreamIdRef.current = null;
          const context = playbackContextRef.current;
          nextPlaybackTimeRef.current = context?.currentTime || 0;
        }
      }
    });

    socket.on('sports:audio:chunk', ({ fixtureId, streamId, encoding, sampleRate, chunk }) => {
      console.log('[Web Dock 🔊] Audio chunk received:', {
        fixtureId,
        unmutedFixtureId: unmutedFixtureIdRef.current,
        chunkLength: chunk?.length,
      });
      if (!chunk || encoding !== LIVE_AUDIO_ENCODING) return;
      if (!unmutedFixtureIdRef.current || fixtureId !== unmutedFixtureIdRef.current) {
        return; // Muted or listening to another match
      }
      if (fixtureId === activeFixtureIdRef.current) {
        setHasLiveAudio(true);
      }

      try {
        let context = playbackContextRef.current;
        if (!context) context = initAudioPlayback();
        if (!context) return;
        if (playbackStreamIdRef.current !== streamId) {
          playbackStreamIdRef.current = streamId;
          nextPlaybackTimeRef.current = context.currentTime + 0.04;
        }
        context.resume().catch(() => {});
        const samples = decodePcm16Chunk(chunk);
        if (samples.length === 0) return;
        const rate = Number(sampleRate) || 16000;
        const buffer = context.createBuffer(1, samples.length, rate);
        buffer.copyToChannel(samples, 0);
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(context.destination);

        const now = context.currentTime;
        // Bound latency after a backgrounded/throttled tab. Live commentary
        // should resume live, not replay seconds of stale queued speech.
        if (nextPlaybackTimeRef.current < now || nextPlaybackTimeRef.current > now + 0.75) {
          nextPlaybackTimeRef.current = now + 0.04;
        }
        source.start(nextPlaybackTimeRef.current);
        nextPlaybackTimeRef.current += buffer.duration;
      } catch {
        /* Ignore one malformed packet; later PCM packets remain independent. */
      }
    });

    return () => {
      resetAudioPlayback();
      socket.emit('sports:live:leave');
      socket.removeAllListeners();
      socket.disconnect();
      audioSocketRef.current = null;
    };
  }, []);

  // Re-render once a minute so the elapsed clock stays honest.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const clamp = useCallback((x, y) => {
    const el = panelRef.current;
    const w = el?.offsetWidth || 320;
    const h = el?.offsetHeight || 120;
    return {
      x: Math.max(8, Math.min(x, window.innerWidth - w - 8)),
      y: Math.max(8, Math.min(y, window.innerHeight - h - 8)),
    };
  }, []);

  // Pointer events (not mouse) so dragging works with touch and pen too.
  const drag = {
    onPointerDown: (e) => {
      if (e.button != null && e.button !== 0) return;
      const el = panelRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, moved: false };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e) => {
      if (!dragRef.current) return;
      e.preventDefault();
      dragRef.current.moved = true;
      setPos(clamp(e.clientX - dragRef.current.dx, e.clientY - dragRef.current.dy));
    },
    onPointerUp: (e) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      setPos((p) => { if (p) write(POS_KEY, p); return p; });
    },
    onPointerCancel: (e) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    },
  };

  // Re-clamp without churning renders: returning the SAME object when the
  // position is already legal stops a ResizeObserver → setPos → render
  // loop, and leaves a dock the user parked mid-screen where they put it.
  const reclamp = useCallback(() => {
    setPos((p) => {
      if (!p) return p;
      const c = clamp(p.x, p.y);
      if (c.x === p.x && c.y === p.y) return p;
      // Persist the corrected spot, or the stale off-screen one would be
      // restored on the next visit and need correcting all over again.
      write(POS_KEY, c);
      return c;
    });
  }, [clamp]);

  // A resize can strand a docked panel off-screen entirely.
  useEffect(() => {
    if (!pos) return undefined;
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [pos, reclamp]);

  // The panel's OWN size changes too, and that was the bigger hole: a
  // dragged position is the panel's top-left corner, clamped against the
  // size it had at the time. Expanding the collapsed bar makes it several
  // times taller and wider, so a dock parked near the bottom-right grew
  // straight off the screen — the expanded panel hung past the edge with
  // its controls unreachable. Watching the element itself pulls it back
  // inside on expand, on collapse, and whenever its content reflows;
  // near the corner that lands it flush in the bottom-right.
  useEffect(() => {
    if (!pos) return undefined;
    const el = panelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(reclamp);
    ro.observe(el);
    return () => ro.disconnect();
    // `minimised` re-runs this so the observer follows the panel element,
    // which is a DIFFERENT node in the collapsed and expanded branches.
  }, [pos, minimised, reclamp]);

  const setMin = (v) => { setMinimised(v); write(MIN_KEY, v); };

  const safeIdx = Math.min(idx, Math.max(0, matches.length - 1));
  const match = matches[safeIdx];
  if (!match) return null;

  const go = (by) => setIdx((i) => {
    const n = matches.length;
    return ((Math.min(i, n - 1) + by) % n + n) % n;
  });

  const scoreFor = match.result?.scoreFor ?? 0;
  const scoreAgainst = match.result?.scoreAgainst ?? 0;
  const many = matches.length > 1;

  const placement = pos ? { left: pos.x, top: pos.y } : undefined;
  const baseCls = pos ? 'fixed z-40' : 'fixed z-40 bottom-20 md:bottom-4 right-3 md:right-4';

  /**
   * ONE small button to move to the next match, rather than a prev/next
   * pair with a counter wedged between them — that group ate a third of
   * the collapsed bar and read as clutter. Present in both states.
   */
  const Switcher = () => !many ? null : (
    <button
      type="button"
      onClick={() => go(1)}
      className={`shrink-0 inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full border transition-colors ${
        match.isLive
          ? 'bg-red-50 border-red-100 text-red-600 hover:bg-red-100'
          : 'bg-secondary-background border-border-primary text-text-secondary hover:bg-border-primary/40'
      }`}
      title={`Switch match — ${safeIdx + 1} of ${matches.length} today`}
      aria-label={`Switch match, showing ${safeIdx + 1} of ${matches.length}`}
      data-testid="live-dock-switch"
      {...noDrag}
    >
      <ArrowLeftRight size={11} />
      <span className="text-[10px] font-bold tabular-nums" data-testid="live-dock-position">
        {safeIdx + 1}/{matches.length}
      </span>
    </button>
  );

  // ── Collapsed: one row, still draggable, still switchable, still named.
  if (minimised) {
    return (
      <div
        ref={panelRef}
        style={placement}
        className={`${baseCls} max-w-[calc(100vw-1.5rem)]`}
        data-testid="live-score-dock-pill"
      >
        <div
          className={`flex items-center gap-2 rounded-full border bg-white pl-2 pr-1 py-1.5 shadow-lg cursor-grab active:cursor-grabbing touch-none select-none ${match.isLive ? 'border-red-200' : 'border-border-primary'}`}
          {...drag}
          data-testid="live-dock-drag-handle"
          title="Drag to move"
        >
          <GripVertical size={12} className="text-red-300 shrink-0" />
          <StatusTag match={match} compact />
          {/* The MATCH NAME, not the two team names. It is what people call
              the game ("Inter-House Final"), it fits on one line, and it
              stays unambiguous when one team plays twice in a day. */}
          <span
            className="text-[11px] font-medium text-text-secondary truncate min-w-0"
            title={`${match.title} — ${match.teamName} vs ${match.opponent || 'opponent'}`}
            data-testid="live-dock-pill-name"
          >
            {match.title}
          </span>
          <span className="text-sm font-bold tabular-nums text-text-primary shrink-0" data-testid={`live-dock-score-${match.id}`}>
            {scoreFor}–{scoreAgainst}
          </span>
          <Switcher />
          <button
            type="button"
            onClick={() => setMin(false)}
            className="h-6 w-6 grid place-items-center rounded-full text-text-muted hover:bg-red-100 shrink-0"
            aria-label="Expand live scores"
            data-testid="live-dock-expand"
            {...noDrag}
          >
            <ChevronUp size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ── Expanded.
  return (
    <div
      ref={panelRef}
      style={placement}
      className={`${baseCls} w-[min(21rem,calc(100vw-1.5rem))]`}
      data-testid="live-score-dock"
    >
      <div className={`rounded-2xl border bg-white shadow-xl overflow-hidden ${match.isLive ? 'border-red-200' : 'border-border-primary'}`}>
        <div
          className={`flex items-center gap-2 px-2.5 py-2 border-b cursor-grab active:cursor-grabbing touch-none select-none ${match.isLive ? 'bg-red-50/70 border-red-100' : 'bg-secondary-background/60 border-border-primary'}`}
          {...drag}
          data-testid="live-dock-drag-handle"
          title="Drag to move"
        >
          <GripVertical size={13} className="text-red-300 shrink-0" />
          <StatusTag match={match} />
          <Switcher />
          <span className="flex-1" />
          <span className="flex items-center gap-0.5 shrink-0" {...noDrag}>
            <button
              type="button"
              onClick={() => {
                if (unmutedFixtureId === activeMatch?.id) {
                  unmutedFixtureIdRef.current = null;
                  setUnmutedFixtureId(null);
                  resetAudioPlayback();
                } else {
                  const fixtureId = activeMatch?.id;
                  // Socket events can return before React renders the state
                  // update. Change the event-handler ref synchronously so the
                  // first independent PCM packet is accepted immediately.
                  unmutedFixtureIdRef.current = fixtureId;
                  setUnmutedFixtureId(fixtureId);
                  resetAudioPlayback();
                  // This runs in the click gesture, allowing browsers with
                  // autoplay protection to resume Web Audio immediately.
                  initAudioPlayback();
                  audioSocketRef.current?.emit('sports:audio:listen', {
                    fixtureId,
                  });
                }
              }}
              className={`h-6 px-1.5 flex items-center gap-1 rounded text-[10px] font-semibold transition-colors ${
                isMuted
                  ? 'text-text-muted hover:bg-slate-100'
                  : hasLiveAudio
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'text-red-600 hover:bg-red-100'
              }`}
              title={isMuted ? 'Unmute Live Voice Commentary' : 'Mute Live Voice Commentary'}
              data-testid="live-dock-audio-mute-toggle"
            >
              {isMuted ? <VolumeX size={13} /> : <Volume2 size={13} />}
              {hasLiveAudio && !isMuted && <span>Live Audio</span>}
            </button>
            <button type="button" onClick={() => setOpen((o) => !o)} className="h-6 w-6 grid place-items-center rounded text-text-muted hover:bg-red-100" aria-label={open ? 'Hide commentary' : 'Show commentary'} data-testid="live-dock-toggle">
              <MessageSquare size={13} className={open ? 'text-red-600' : ''} />
            </button>
            {/* Minimise, never dismiss — the pill is always a click away. */}
            <button type="button" onClick={() => setMin(true)} className="h-6 w-6 grid place-items-center rounded text-text-muted hover:bg-red-100" aria-label="Minimise live scores" data-testid="live-dock-minimise">
              <ChevronDown size={14} />
            </button>
          </span>
        </div>

        <div className="px-3 pt-2.5 pb-3" data-testid={`live-dock-match-${match.id}`}>
          <div className="text-xs font-semibold text-text-primary truncate">{match.title}</div>

          {/* Each number under its own team, so nobody has to work out which
              half of "134–96" is theirs. */}
          <div className="mt-2 flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-text-muted truncate" title={match.teamName}>{match.teamName}</div>
              <div className="text-2xl font-bold tabular-nums text-text-primary leading-none mt-0.5" data-testid={`live-dock-score-for-${match.id}`}>
                {scoreFor}
              </div>
            </div>
            <span className="text-[10px] text-text-muted pb-1 shrink-0">vs</span>
            <div className="min-w-0 flex-1 text-right">
              <div className="text-[10px] text-text-muted truncate" title={match.opponent}>{match.opponent || 'Opponent'}</div>
              <div className={`text-2xl font-bold tabular-nums leading-none mt-0.5 ${match.isLive ? 'text-red-600' : 'text-text-primary'}`} data-testid={`live-dock-score-against-${match.id}`}>{scoreAgainst}</div>
            </div>
          </div>

          <div className="mt-2 pt-2 border-t border-border-primary flex items-center gap-1.5 text-[10px] text-text-muted" data-testid={`live-dock-elapsed-${match.id}`}>
            {match.isLive ? (
              <>
                {match.liveNote && (
                  <span className="rounded bg-secondary-background px-1.5 py-0.5 font-medium text-text-secondary shrink-0">
                    {match.liveNote}
                  </span>
                )}
                <span className="truncate">
                  Started {clock(match.liveStartedAt)}
                  {elapsed(match.liveStartedAt) ? ` · ${elapsed(match.liveStartedAt)} in` : ' · just started'}
                </span>
              </>
            ) : (
              <>
                <span className="rounded bg-secondary-background px-1.5 py-0.5 font-medium text-text-secondary shrink-0">
                  Full time
                </span>
                <span className="truncate" data-testid="live-dock-winner">
                  {match.winnerName
                    ? (match.winnerName === 'Draw'
                      ? `Drawn · ${clock(match.completedAt)}`
                      : `${match.winnerName} won · ${clock(match.completedAt)}`)
                    : `Finished ${clock(match.completedAt)}`}
                </span>
              </>
            )}
          </div>
        </div>

        {open && (
          <div className="border-t border-border-primary px-3 py-2 bg-secondary-background/30" data-testid="live-dock-commentary">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">
              <MessageSquare size={11} /> Commentary
            </div>
            {match.commentary?.length ? (
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {match.commentary.map((c, i) => (
                  <li key={`${c.at}-${i}`} className="text-[11px] flex gap-2" data-testid="live-dock-commentary-item">
                    <span className="shrink-0 font-semibold tabular-nums text-text-muted">
                      {c.scoreFor ?? 0}–{c.scoreAgainst ?? 0}
                    </span>
                    <span className="text-text-primary">{c.text}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-text-muted">No commentary yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
